// src/pages/DashboardEcommerce/index.tsx
// Mục 24, REQUIRE_FIXBUGS.md — Dashboard tổng quan LMS, thay thế hoàn toàn nội dung ecommerce mẫu
// (Widget/Revenue/SalesByLocations/BestSellingProducts/... — toàn bộ là mock data thương mại điện
// tử còn sót lại từ template gốc, không liên quan gì tới nghiệp vụ xổ số) bằng số liệu THẬT lấy từ:
// - ticket-service (Reports/DashboardSummary): kỳ phát hành, giao vé, thu hồi vé ế, kết quả mở
//   thưởng, doanh thu ước tính (xem ReportConstants.TicketPrice — giá vé cố định 10.000đ/vé theo
//   xác nhận trực tiếp của người dùng, CHƯA có finance-service/giá vé theo kỳ thật).
// - system-service (qua loadAgentsWithCache dùng chung toàn app): số đại lý.
// - payout-service (getClaimDossiersApi): số hồ sơ trả thưởng.
// Đây là trang "/" mặc định (Navigate to "/dashboard" ở allRoutes.tsx).

import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { toast } from "react-toastify";
import moment from "moment";

import BreadCrumb from "Components/Common/BreadCrumb";
import DataTableShell from "Components/Common/DataTableShell";
import {
  getDashboardSummaryApi,
  getClaimDossiersApi,
} from "helpers/fakebackend_helper";
import { loadAgentsWithCache } from "helpers/agentLookup";
import { useBrandingTitle } from "../../hooks";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('dashboard_ecommerce');

interface PlanQuantityPoint {
  planCode: string;
  quantity: number;
}

interface DeliveredRecalledPoint {
  planCode: string;
  delivered: number;
  recalled: number;
}

interface DrawResultRow {
  region: string;
  prizeLevel: string;
  prizeAmount: number;
  numberOfPrizes: number;
  winningNumbers: string;
  actualDrawDate: string;
}

interface DashboardSummary {
  totalIssuancePlans: number;
  latestPlanId?: number | null;
  latestPlanCode?: string | null;
  latestPlanDeliveredQuantity: number;
  latestPlanIssuedQuantity: number;
  latestPlanRevenue: number;
  totalRevenue: number;
  issuedLast5Plans: PlanQuantityPoint[];
  deliveredVsRecalledLast5Plans: DeliveredRecalledPoint[];
  latestDrawResults: DrawResultRow[];
}

const formatNumber = (value?: number | null) =>
  typeof value === "number" ? value.toLocaleString("vi-VN") : "0";

const formatCurrency = (value?: number | null) =>
  typeof value === "number" ? st('value_d_text', { value: value.toLocaleString("vi-VN") }) : "-";

// Mục 16c (Update), README_UPDATE.md — tách chuỗi số trúng thành từng số riêng lẻ để hiển thị
// mỗi số 1 ô trong popup chi tiết. Chuỗi lưu ở BE có thể phân tách bằng dấu phẩy, chấm phẩy
// hoặc khoảng trắng nên tách theo mọi ký tự phân cách đó cho chắc chắn.
const parseWinningNumbers = (winningNumbers?: string | null): string[] =>
  (winningNumbers || "")
    .split(/[,;\s]+/)
    .map((n) => n.trim())
    .filter((n) => n.length > 0);

const StatCard: React.FC<{
  icon: string;
  label: string;
  value: React.ReactNode;
  color: string;
}> = ({ icon, label, value, color }) => (
  <Col xl={3} md={6}>
    <Card className="card-animate">
      <CardBody>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <p className="text-uppercase fw-medium text-muted mb-0">{label}</p>
            <h4 className="mb-0 mt-1">{value}</h4>
          </div>
          <div className="flex-shrink-0">
            <span
              className={`avatar-title rounded fs-3 bg-${color}-subtle text-${color}`}
            >
              <i className={icon}></i>
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const DashboardEcommerce: React.FC = () => {
  useBrandingTitle(st('bang_dieu_khien_text'));

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [agentCount, setAgentCount] = useState<number | null>(null);
  const [claimDossierCount, setClaimDossierCount] = useState<number | null>(
    null,
  );
  // Mục 16c, README_UPDATE.md — dòng kết quả đang được xem chi tiết số trúng đầy đủ trong popup
  const [selectedDrawResult, setSelectedDrawResult] =
    useState<DrawResultRow | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [summaryRes, agents] = await Promise.all([
          getDashboardSummaryApi(),
          loadAgentsWithCache(),
        ]);
        setSummary(
          ((summaryRes as any)?.data ?? summaryRes) as DashboardSummary,
        );
        setAgentCount(agents.length);
      } catch (error: any) {
        toast.error(error?.message || st('khong_the_tai_so_lieu_dashboard_message'));
      } finally {
        setLoading(false);
      }

      try {
        const dossierRes = await getClaimDossiersApi({
          pageIndex: 1,
          pageSize: 1,
        });
        const payload = (dossierRes as any)?.data ?? dossierRes;
        setClaimDossierCount(payload?.totalItems ?? payload?.totalCount ?? 0);
      } catch {
        // Không chặn cả trang nếu payout-service lỗi — chỉ ô này hiện "-" (xem render bên dưới).
        setClaimDossierCount(null);
      }
    };

    load();
  }, []);

  const issuedChartSeries = useMemo(
    () => [
      {
        name: st('so_ve_phat_hanh_text'),
        data: (summary?.issuedLast5Plans || []).map((p) => p.quantity),
      },
    ],
    [summary],
  );

  const issuedChartOptions: any = useMemo(
    () => ({
      chart: { type: "bar", height: 300, toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 4 } },
      dataLabels: { enabled: false },
      colors: ["#405189"],
      xaxis: {
        categories: (summary?.issuedLast5Plans || []).map((p) => p.planCode),
      },
      yaxis: { labels: { formatter: (v: number) => formatNumber(v) } },
      tooltip: { y: { formatter: (v: number) => st('value_ve_text', { value: formatNumber(v) }) } },
    }),
    [summary],
  );

  const compareChartSeries = useMemo(
    () => [
      {
        name: st('so_ve_da_giao_text'),
        data: (summary?.deliveredVsRecalledLast5Plans || []).map(
          (p) => p.delivered,
        ),
      },
      {
        name: st('so_ve_da_hoan_e_text'),
        data: (summary?.deliveredVsRecalledLast5Plans || []).map(
          (p) => p.recalled,
        ),
      },
    ],
    [summary],
  );

  const compareChartOptions: any = useMemo(
    () => ({
      chart: { type: "bar", height: 300, toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
      dataLabels: { enabled: false },
      colors: ["#0ab39c", "#f06548"],
      xaxis: {
        categories: (summary?.deliveredVsRecalledLast5Plans || []).map(
          (p) => p.planCode,
        ),
      },
      yaxis: { labels: { formatter: (v: number) => formatNumber(v) } },
      legend: { show: true, position: "top" },
      tooltip: { y: { formatter: (v: number) => st('value_ve_text', { value: formatNumber(v) }) } },
    }),
    [summary],
  );

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5">
          <Spinner color="primary" />
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('bang_dieu_khien_text')} pageTitle={st('tong_quan_text')} />

          <Row>
            <StatCard
              icon="ri-file-list-3-line"
              label={st('so_ky_phat_hanh_label')}
              value={formatNumber(summary?.totalIssuancePlans)}
              color="primary"
            />
            <StatCard
              icon="ri-store-2-line"
              label={st('so_dai_ly_label')}
              value={agentCount === null ? "-" : formatNumber(agentCount)}
              color="info"
            />
            <StatCard
              icon="ri-truck-line"
              label={st('so_ve_da_ban_giao_ky_text', { value: summary?.latestPlanCode || st('gan_nhat_text') })}
              value={formatNumber(summary?.latestPlanDeliveredQuantity)}
              color="success"
            />
            <StatCard
              icon="ri-ticket-2-line"
              label={st('so_ve_phat_hanh_ky_value_text', { value: summary?.latestPlanCode || st('gan_nhat_text') })}
              value={formatNumber(summary?.latestPlanIssuedQuantity)}
              color="warning"
            />
          </Row>

          <Row>
            <StatCard
              icon="ri-money-dollar-circle-line"
              label={st('doanh_thu_uoc_tinh_toan_he_label')}
              value={formatCurrency(summary?.totalRevenue)}
              color="success"
            />
            <StatCard
              icon="ri-hand-coin-line"
              label={st('doanh_thu_uoc_tinh_ky_value_text', { value: summary?.latestPlanCode || st('gan_nhat_text') })}
              value={formatCurrency(summary?.latestPlanRevenue)}
              color="primary"
            />
            <StatCard
              icon="ri-file-shield-2-line"
              label={st('so_ho_so_tra_thuong_label')}
              value={
                claimDossierCount === null
                  ? "-"
                  : formatNumber(claimDossierCount)
              }
              color="info"
            />
            <StatCard
              icon="ri-information-line"
              label={st('ghi_chu_doanh_thu_label')}
              value={
                <span className="fs-13 text-muted">
                  {st('tam_tinh_10_000d_ve_co_text')}
                </span>
              }
              color="warning"
            />
          </Row>

          <Row>
            <Col xl={6}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {st('so_ve_phat_hanh_5_ky_title')}
                  </h4>
                </CardHeader>
                <CardBody>
                  {(summary?.issuedLast5Plans || []).length === 0 ? (
                    <p className="text-muted text-center py-5 mb-0">
                      {st('chua_co_du_lieu_text')}
                    </p>
                  ) : (
                    <ReactApexChart
                      options={issuedChartOptions}
                      series={issuedChartSeries}
                      type="bar"
                      height={300}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col xl={6}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {st('ve_giao_ve_hoan_5_ky_title')}
                  </h4>
                </CardHeader>
                <CardBody>
                  {(summary?.deliveredVsRecalledLast5Plans || []).length ===
                  0 ? (
                    <p className="text-muted text-center py-5 mb-0">
                      {st('chua_co_du_lieu_text')}
                    </p>
                  ) : (
                    <ReactApexChart
                      options={compareChartOptions}
                      series={compareChartSeries}
                      type="bar"
                      height={300}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {st('ket_qua_ky_mo_thuong_moi_title')}
                  </h4>
                </CardHeader>
                <CardBody className="pt-0">
                  {(summary?.latestDrawResults || []).length === 0 ? (
                    <p className="text-muted text-center py-5 mb-0">
                      {st('chua_co_ket_qua_mo_thuong_text')}
                    </p>
                  ) : (
                    <>
                      <p className="text-muted mb-3">
                        {st('ngay_mo_thuong_text')}{" "}
                        <strong>
                          {moment(
                            summary?.latestDrawResults[0]?.actualDrawDate,
                          ).format("DD/MM/YYYY")}
                        </strong>
                      </p>
                      <DataTableShell
                        headerRow={
                          <tr>
                            <th>{st('khu_vuc_column')}</th>
                            <th>{st('giai_column')}</th>
                            <th className="text-end">{st('gia_tri_giai_column')}</th>
                            <th className="text-end">{st('so_luong_giai_column')}</th>
                            <th>{st('so_trung_column')}</th>
                          </tr>
                        }
                      >
                        {(summary?.latestDrawResults || []).map(
                          (row, index) => (
                            <tr key={index}>
                              <td>{row.region}</td>
                              <td>{row.prizeLevel}</td>
                              <td className="text-end">
                                {formatCurrency(row.prizeAmount)}
                              </td>
                              <td className="text-end">
                                {formatNumber(row.numberOfPrizes)}
                              </td>
                              <td
                                className="fw-medium"
                                style={{ cursor: "pointer" }}
                                onClick={() => setSelectedDrawResult(row)}
                              >
                                {parseWinningNumbers(row.winningNumbers).length}{" "}
                                {st('so_trung_text')}{" "}
                                <i className="ri-eye-line text-muted"></i>
                              </td>
                            </tr>
                          ),
                        )}
                      </DataTableShell>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Mục 16c (Update), README_UPDATE.md — popup hiển thị đầy đủ số trúng, mỗi số 1 ô nền xanh */}
      <Modal
        isOpen={!!selectedDrawResult}
        toggle={() => setSelectedDrawResult(null)}
        centered
      >
        <ModalHeader toggle={() => setSelectedDrawResult(null)}>
          {st('chi_tiet_so_trung_title')}
        </ModalHeader>
        <ModalBody>
          {selectedDrawResult && (
            <>
              <p className="text-muted mb-3">
                {st('khu_vuc_text')} <strong>{selectedDrawResult.region}</strong> {st('giai_text')}{" "}
                <strong>{selectedDrawResult.prizeLevel}</strong>
              </p>
              <div className="d-flex flex-wrap gap-2">
                {parseWinningNumbers(selectedDrawResult.winningNumbers).map(
                  (number, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-2 rounded"
                      style={{
                        backgroundColor: "#eaf6ec",
                        color: "#000",
                        fontWeight: "bold",
                      }}
                    >
                      {number}
                    </span>
                  ),
                )}
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default DashboardEcommerce;
